package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import org.jetbrains.compose.web.dom.Caption
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

private val table = Bem("admin-data-table")

/**
 * A column of [AdminDataTable]: [header] names it, also for screen readers when [headerHidden] (e.g. an action column);
 * [number] right-aligns counts and running numbers; [cell] draws the row's value.
 */
class DataTableColumn<T>(
    val header: String,
    val number: Boolean = false,
    val headerHidden: Boolean = false,
    val cell: @Composable (T) -> Unit,
)

/**
 * A plain table for records with several comparable columns (people, performances), in a frame that scrolls sideways
 * on narrow screens instead of truncating. [caption] names the table for screen readers.
 *
 * Selecting is the consumer's: put a button or link into a cell and pass the [selected] key, which marks its row with
 * `aria-selected`, and [recent], which marks the row that was open last (see [AdminSplitView]). Inside a card, set
 * [inCard] so the frame drops its own border.
 */
@Composable
fun <T, K : Any> AdminDataTable(
    rows: List<T>,
    key: (T) -> K,
    columns: List<DataTableColumn<T>>,
    caption: String,
    selected: K? = null,
    recent: K? = null,
    inCard: Boolean = false,
) {
    Div({ classes(*listOfNotNull(table.block, table.modifier("in-card").takeIf { inCard }).toTypedArray()) }) {
        Table({ classes(table.element("table").className) }) {
            Caption({ classes("admin-visually-hidden") }) { Text(caption) }
            Thead {
                Tr {
                    columns.forEach { column ->
                        Th({
                            classes(table.element("header-cell").className)
                            attr("scope", "col")
                        }) {
                            if (column.headerHidden) {
                                Span({ classes("admin-visually-hidden") }) { Text(column.header) }
                            } else {
                                Text(column.header)
                            }
                        }
                    }
                }
            }
            Tbody {
                rows.forEach { row ->
                    val rowKey = key(row)
                    key(rowKey) {
                        Tr({
                            classes(table.element("row").className)
                            if (selected != null) {
                                attr("aria-selected", (rowKey == selected).toString())
                            }
                            if (rowKey == recent) {
                                attr("data-recent", "true")
                            }
                        }) {
                            columns.forEach { column ->
                                Td({
                                    classes(
                                        *listOfNotNull(
                                            table.element("cell").className,
                                            table.element("cell").modifier("number").takeIf { column.number },
                                        ).toTypedArray(),
                                    )
                                }) { column.cell(row) }
                            }
                        }
                    }
                }
            }
        }
    }
}
